// src/belediye/services/bildirimApi.js
//
// FAZ-33 — Merkezî Bildirim API'si (bildirim_app).
// - belediyeApi instance'ını reuse eder (token refresh + X-Unit-Slug davranışı korunur).
// - Backend uçları:
//     GET  /bildirimler/notifications/?unread=1&limit=100&since_id=42  -> {results, unread_count}
//     POST /bildirimler/notifications/mark-read/  { ids:[...] } | { all:true }  -> {updated}
//     GET  /bildirimler/preferences/  -> tercih objesi
//     PUT  /bildirimler/preferences/  -> güncellenmiş tercih
//
// Not: Bildirim ÇEKİRDEK altyapıdır (ModuleToggle YOK); feature-flag probe YOK.
// Kaynak modüller (close_approval, gorev...) kendi flag+izin kapılarını backend'de uygular.

import belediyeApi from "./belediyeApi";

const BASE = "/bildirimler";

const DEBUG = String(process.env.REACT_APP_DEBUG_BILDIRIM || "").trim() === "1";
const log = (...a) => {
  if (DEBUG) console.log("[bildirimApi]", ...a);
};

const stripUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
};

// Yeni Notification satırını eski (bell/slice) sözleşmesine map'ler → mevcut
// bileşenler DEĞİŞMEDEN çalışır (message/application_id/created_at_iso alias).
const normalizeItem = (n) => {
  if (!n || typeof n !== "object") return n;
  const payload = n.payload || {};
  return {
    ...n,
    message: n.body || n.title || "",
    created_at_iso: n.created_at || null,
    application_id:
      n.application_id ??
      payload.application_id ??
      (n.target_app_label === "core_app" ? Number(n.target_object_id) || null : null),
    close_request_id: payload.close_request_id ?? null,
  };
};

/**
 * Bildirim feed'i.
 * @returns {Promise<{items: Array, unreadCount: number}>}
 */
export const getNotifications = async ({
  unreadOnly = false,
  limit = 50,
  sinceId,
  source,
} = {}) => {
  const params = stripUndefined({
    unread: unreadOnly ? 1 : undefined,
    // FAZ-77: tavan 200 → 500. Sayfadaki "Daha fazla yükle" 500'e çıkıyordu ama
    // hem burada hem backend'de 200'e kırpılıyordu → düğme 200'den sonra hiç
    // iş yapmıyor, kullanıcı aynı listeye bakıyordu (sessiz tavan).
    limit: Math.max(1, Math.min(Number(limit) || 50, 500)),
    since_id: sinceId ? Number(sinceId) : undefined,
    // FAZ-77: segment sekmesi kaynağını sunucuya söyler ("duyuru,sistem,sms").
    source: source || undefined,
  });
  const { data } = await belediyeApi.get(`${BASE}/notifications/`, { params });
  const results = Array.isArray(data?.results) ? data.results : [];
  log("getNotifications:ok", { count: results.length, unread: data?.unread_count });
  return {
    items: results.map(normalizeItem),
    unreadCount: Number(data?.unread_count || 0),
    // FAZ-77: sekme rozetleri DB'den — {"gorev": 4, "duyuru": 1, ...}
    unreadBySource:
      data?.unread_by_source && typeof data.unread_by_source === "object"
        ? data.unread_by_source
        : {},
  };
};

/**
 * Okundu işaretle. ids listesi ya da {all:true}.
 * `source` verilirse "tümü" YALNIZ o kaynakları kapsar (açık sekme).
 */
export const markNotificationsRead = async (ids = [], { all = false, source } = {}) => {
  if (all) {
    const { data } = await belediyeApi.post(
      `${BASE}/notifications/mark-read/`,
      stripUndefined({ all: true, source: source || undefined })
    );
    return data;
  }
  const cleaned = (Array.isArray(ids) ? ids : [])
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0);
  if (!cleaned.length) return { updated: 0 };
  const { data } = await belediyeApi.post(`${BASE}/notifications/mark-read/`, { ids: cleaned });
  return data;
};

/** Kullanıcı bildirim tercihleri. */
export const getPreferences = async () => {
  const { data } = await belediyeApi.get(`${BASE}/preferences/`);
  return data;
};

export const updatePreferences = async (patch = {}) => {
  const { data } = await belediyeApi.put(`${BASE}/preferences/`, stripUndefined(patch));
  return data;
};

// ── FAZ-84 — TELEFON BİLDİRİMİ (WEB PUSH) ───────────────────────────────────

/** VAPID açık anahtarı + özelliğin açık olup olmadığı.
 *  Anahtar build değişkeninde DEĞİL uçta durur: bu projede kod ancak yeniden
 *  derlemeyle canlıya çıkıyor; anahtarı bundle'a gömmek her değişikliği bir
 *  rebuild'e bağlardı. (Anahtar zaten AÇIK anahtardır.) */
export const pushAnahtarAl = async () => {
  const { data } = await belediyeApi.get(`${BASE}/push/anahtar/`);
  return data;
};

/** Uzaktan kapatma bayrağı (kill-switch) + beklenen nöbetçi sürümü. */
export const pushDurumAl = async () => {
  const { data } = await belediyeApi.get(`${BASE}/push/durum/`);
  return data;
};

/** Cihaz aboneliğini kaydet/güncelle (idempotent). */
export const pushAboneOl = async ({ endpoint, keys, sw_surum, standalone }) => {
  const { data } = await belediyeApi.post(`${BASE}/push/abone/`, {
    endpoint,
    keys,
    sw_surum,
    standalone,
  });
  return data;
};

/** Bu cihazın aboneliğini kapat (satır silinmez, pasife alınır). */
export const pushAbonelikSil = async (endpoint) => {
  const { data } = await belediyeApi.post(`${BASE}/push/abonelik-sil/`, { endpoint });
  return data;
};

// NOT: "test bildirimi gönder" ucu ve düğmesi 2026-08-01'de KALDIRILDI
// (kullanıcı: "kişiler için test bildirimi falan olmasın, gerekmez"). Kişi,
// bildirimi açtığı anda ilk gerçek olayda çalıştığını zaten görüyor; çağrısı
// kalmayan bir uç ölü koddur ve internete açık gereksiz bir yüzeydir.

/** FAZ-101 — SUNUCU BU CİHAZIN ABONELİĞİNİ GÖRÜYOR MU?
 *
 *  Abonelik iki tarafta birden yaşar: tarayıcıda bir nesne, sunucuda bir satır.
 *  İkisi ayrışabilir (uç 410 verip pasife alındı, ortak telefonda başkasına
 *  devredildi, kayıt hiç ulaşmadı) ve o hâllerde ekran "Açık" yazıp tek bildirim
 *  gelmez. Bu uç sunucunun görüşünü söyler: "aktif" | "pasif" | "yok".
 *
 *  FAZ-102 NOTU: bu uç eskiden cihazın kurulum/izin hâlini de KAYDEDİYORDU
 *  (yönetici raporu için). Rapor kaldırıldı, kayıt da kaldırıldı — çağıranı
 *  kalmayan veri toplamak, toplanmaması gereken veridir. Uç artık yalnız
 *  sorar, hiçbir şey yazmaz. */
export const pushAbonelikKontrol = async (endpoint) => {
  const { data } = await belediyeApi.post(`${BASE}/push/abonelik-kontrol/`, {
    endpoint,
  });
  return data;
};

/** Tek bildirim satırı — telefon bildirimi köprü rotası kullanır. */
export const getNotification = async (id) => {
  const { data } = await belediyeApi.get(`${BASE}/notifications/${Number(id)}/`);
  return normalizeItem(data);
};

/**
 * Manuel duyuru gönder (bildirim.duyuru.gonder izni + scope-güvenli backend).
 * body: { type: "broadcast"|"unit"|"role"|"users", title, body, level,
 *         scheduled_for?, unit_id?, role?, user_ids? }
 */
export const sendDuyuru = async ({
  type = "broadcast",
  title,
  body = "",
  level = "info",
  scheduledFor,
  unitId,
  role,
  userIds,
} = {}) => {
  const payload = stripUndefined({
    type,
    title,
    body,
    level,
    scheduled_for: scheduledFor || undefined,
    unit_id: unitId || undefined,
    role: role || undefined,
    user_ids: Array.isArray(userIds) ? userIds : undefined,
  });
  const { data } = await belediyeApi.post(`${BASE}/duyuru/`, payload);
  return data;
};
